<script>
import MixedGridFilters from '@phoenix/mixins/components/mixedGrid/MixedGridFilters';
import MixedGridSortSelect from './MixedGridSortSelect';
import MixedGridFiltersItemList from './MixedGridFiltersItemList';
import MixedGridFiltersItemListWithTooltip from './MixedGridFiltersItemListWithTooltip';
import MixedGridFiltersItemRange from './MixedGridFiltersItemRange';
import MixedGridFiltersSelected from './MixedGridFiltersSelected';
import MixedGridFiltersItemColor from '@maison/components/mixedGrid/MixedGridFiltersItemColor.vue';
import MixedGridFiltersItemRadio from '@maison/components/mixedGrid/MixedGridFiltersItemRadio.vue';
import MixedGridFiltersItemSelect from '@maison/components/mixedGrid/MixedGridFiltersItemSelect.vue';
import MixedGridFiltersItemReverso from '@maison/components/mixedGrid/MixedGridFiltersItemReverso.vue';
import CTA from '@maison/components/common/CTA.vue';
import Panel from '../common/Panel';
import { PhxIcon, PhxStickyBar, PhxToggle } from '@phx-styleguides/jlc';
import { showPanel } from '@phoenix/helpers/panel-helper';
import { Teleport } from 'vue';

export default {
  components: {
    MixedGridSortSelect,
    MixedGridFiltersItemColor,
    MixedGridFiltersItemList,
    MixedGridFiltersItemListWithTooltip,
    MixedGridFiltersItemRange,
    MixedGridFiltersItemRadio,
    MixedGridFiltersItemSelect,
    MixedGridFiltersSelected,
    MixedGridFiltersItemReverso,
    Panel,
    Teleport,
    CTA,
    PhxIcon,
    PhxStickyBar,
    PhxToggle,
  },

  mixins: [MixedGridFilters],

  inject: {
    hasFindYourFitWidget: {
      default: false,
    },
    reversoFilterId: {
      default: null,
    },
  },

  props: {
    cta: {
      type: Object,
      default: null,
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    customTags: {
      type: Array,
      default: () => [],
    },
    numberProductsByFilters: {
      type: Object,
      default: () => {},
    },
    panelId: {
      type: String,
      default: 'filters-panel',
    },
    showWatchComparator: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['onSortChange', 'onFiltersChanged', 'filtersInitialized', 'onWatchComparatorToggle'],

  data() {
    return {
      watchComparatorChecked: false,
    };
  },

  computed: {
    watchComparatorToggleId() {
      return `watch-comparator-toggle-${this.uniqueId}`;
    },

    watchComparatorMobileToggleId() {
      return `watch-comparator-toggle-mobile-${this.uniqueId}`;
    },

    displayedFilters() {
      const filters = this.filters.filter((item) => item.id !== this.reversoFilterId);

      if (this.shouldDisplayUnclearableFilters) {
        return filters;
      }

      return filters.filter((item) => !this.unclearableFilters.includes(item.id));
    },

    hasReversoFilter() {
      return this.reversoFilter ?? false;
    },

    reversoFilter() {
      return this.getFilterById(this.reversoFilterId);
    },
  },

  methods: {
    openPanel() {
      showPanel(this.panelId);
    },

    subtitleFilter(template) {
      return template === 'range' ? 'mb-md' : 'filters-list-title';
    },

    onWatchComparatorToggle(value) {
      this.watchComparatorChecked = value;
      this.$emit('onWatchComparatorToggle', value);
    },
  },
};
</script>

<template>
  <phx-sticky-bar class="filters-bar" v-bind="$attrs">
    <div class="filters-bar__container container">
      <div class="filters-bar__primary">
        <button
          type="button"
          class="btn btn--default btn--icon btn--white btn--transparent-on-dark"
          data-cy="mixed-grid-filters-panel-open-btn"
          @click="openPanel"
        >
          <span class="btn__text">{{ $t('Product.filters') }}</span>
          <span class="btn__icon">
            <phx-icon icon="filters-20" />
          </span>
        </button>
      </div>

      <div class="filters-bar__secondary">
        <div class="filters-bar__secondary-item">
          <mixed-grid-sort-select v-model="currentSort"></mixed-grid-sort-select>
        </div>

        <div v-if="showWatchComparator" class="filters-bar__secondary-item">
          <div class="toggle-input" data-cy="watch-comparator-toggle">
            <label class="toggle-input__label" :for="watchComparatorToggleId">
              {{ $t('WatchComparator.compare.label') }}
            </label>

            <phx-toggle
              :id="watchComparatorToggleId"
              name="watch-comparator-toggle"
              value=""
              :disabled="false"
              :checked="watchComparatorChecked"
              :aria-label="$t('WatchComparator.toggle.ariaLabel')"
              @change="onWatchComparatorToggle"
            />
          </div>
        </div>

        <div v-if="cta" class="filters-bar__secondary-item">
          <c-t-a v-bind="cta" />
        </div>
      </div>
    </div>

    <div v-if="showWatchComparator" class="filters-bar--watch-comparator">
      <div class="filters-bar__container container">
        <div class="filters-bar__primary">
          <div class="toggle-input" data-cy="watch-comparator-toggle-mobile">
            <label class="toggle-input__label" :for="watchComparatorMobileToggleId">
              {{ $t('WatchComparator.compare.label') }}
            </label>

            <phx-toggle
              :id="watchComparatorMobileToggleId"
              name="watch-comparator-toggle"
              value=""
              :disabled="false"
              :checked="watchComparatorChecked"
              :aria-label="$t('WatchComparator.toggle.ariaLabel')"
              @change="onWatchComparatorToggle"
            />
          </div>
        </div>
      </div>
    </div>

    <teleport to="#teleport-target-panels">
      <panel
        :id="panelId"
        :reverse="false"
        :classes="['products-filters-panel', { 'theme-dark': isDarkMode }]"
        :title="shouldDisplayRemoveAllFilters ? '' : $t('Product.filters')"
        data-cy="mixed-grid-desktop-filters"
      >
        <template v-if="shouldDisplayRemoveAllFilters" #header>
          <button
            type="button"
            class="btn btn--default btn--icon-caption btn--icon"
            data-cy="clear-filters-btn"
            @click="removeAllFilters"
          >
            <span class="btn__icon">
              <phx-icon icon="circular-arrows-16" />
            </span>
            <span class="btn__text">{{ $t('Search.ResetAll') }}</span>
          </button>
        </template>

        <template #content>
          <div v-show="shouldDisplaySellable">
            <h5 class="filters-list-title">{{ $t('Products.filters.quickFilters') }}</h5>
            <ul class="filters-list">
              <li>
                <div class="checkbox">
                  <input
                    id="available-online"
                    v-model="availableOnline"
                    type="checkbox"
                    name="available-online"
                    class="checkbox__input"
                    data-cy="mixed-grid-available-online"
                  />
                  <label for="available-online" class="checkbox__label">
                    {{ $t('Products.filters.availableOnline') }}
                  </label>
                </div>
              </li>
            </ul>
            <hr class="divider" />
          </div>
          <template v-for="(filter, index) in displayedFilters" :key="`filter-${filter.id}`">
            <h5 :class="subtitleFilter(filter.component)">{{ filter.label }}</h5>
            <mixed-grid-filters-item-list
              v-if="filter.component === 'list'"
              :filter="filter"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-list-with-tooltip
              v-if="filter.component === 'list-with-tooltip'"
              :filter="filter"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-range
              v-if="filter.component === 'range'"
              :filter="filter"
              :symbol="getSymbolFromFilter(filter)"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-range-change="onRangeChange"
            />
            <mixed-grid-filters-item-color
              v-if="filter.component === 'color'"
              :filter="filter"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-radio
              v-if="filter.component === 'radio'"
              :filter="filter"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-item-selected="toggleFilter"
            />
            <mixed-grid-filters-item-select
              v-if="filter.component === 'select'"
              :filter="filter"
              :active-filters="activeFilters"
              :empty-filters="emptyFilters"
              @on-item-selected="toggleFilter"
            />

            <template v-if="filter.name === 'watch-size-category' && hasFindYourFitWidget">
              <button class="btn btn--link btn--icon mt-sm" data-modal-open="find-your-fit-modal">
                <span class="btn__text">{{ $t('FindYourFit.button') }}</span>

                <span class="btn__icon">
                  <phx-icon icon="chevron-10" bidirectional />
                </span>
              </button>
            </template>

            <hr v-if="index < displayedFilters.length - 1" class="divider" />
          </template>
        </template>

        <template #footer>
          <button
            type="button"
            class="btn btn--default btn--full btn--negative"
            :data-panel-close="panelId"
            data-cy="mixed-grid-filters-panel-submit-btn"
          >
            <span class="btn__text">{{ $t('Search.applyFilters', { count: productsCount }) }}</span>
          </button>
        </template>
      </panel>
    </teleport>

    <div v-if="hasReversoFilter" class="filters-bar--reverso">
      <div class="filters-bar__container container">
        <div class="filters-bar__primary">
          <label for="reverso-filters">{{ $t('Products.filters.typeOfReverso') }}</label>
        </div>

        <div class="filters-bar__secondary">
          <div class="filters-bar__secondary-item">
            <mixed-grid-filters-item-reverso
              :filter="reversoFilter"
              :active-filters="activeFilters"
              :number-products-by-filters="numberProductsByFilters"
              @on-item-selected="switchFilter"
              @on-remove-filter="removeFilter"
            />
          </div>
        </div>
      </div>
    </div>
  </phx-sticky-bar>

  <div class="container">
    <mixed-grid-filters-selected
      :filters="filters"
      :active-filters="activeFilters"
      :sellable-filter-id="sellableFilterId"
      :unclearable-filters="unclearableFilters"
      :custom-tags="customTags"
      @remove-filter="removeFilter"
      @remove-all-filters="removeAllFilters"
    />
    <div v-if="shouldDisplayProductsCount" class="products__filtered-results h6" data-cy="mixed-grid-products-count">
      {{ productCountLabel }}
    </div>
  </div>
</template>
